import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/appContextCore';
import { IS_TENANT } from '../tenant/config';
import { detectPeriodEnd } from '../utils/periods';
import {
  Upload, Check, AlertCircle, FileSpreadsheet,
  Loader, CheckCircle2, ChevronLeft, Info
} from 'lucide-react';

// ── Period label helper (mirrors RCTIPage.isoToPeriodLabel) ──────────────────
function isoToPeriodLabel(endISO) {
  if (!endISO) return '';
  const end = new Date(endISO + 'T00:00:00');
  if (isNaN(end.getTime())) return '';
  const start = new Date(end);
  start.setDate(end.getDate() - 27);
  const fmt = d => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt(start)} to ${fmt(end)}`;
}

// detectPeriodEnd now lives in src/utils/periods.js (shared with AppContext + RCTIPage).
// A known period-end Saturday on the 28-day grid — used ONLY to align detectPeriodEnd
// when the live Controls anchor hasn't loaded yet (any grid Saturday gives the same
// result). Same value as PERIOD_ANCHOR in TaxInvoicesPage/GCCDReportPage/HourlyDriversPage.
const PERIOD_ANCHOR_STATIC = '2026-05-16';

// ── AP XLSX Column positions ──────────────────────────────────────────────────
// These are FALLBACK positions only. Different AP portals export the columns in
// different orders (e.g. one sender puts "Article Unknown" at the end @18, another
// puts it mid-row @11 which shifts Driver Name, Signoff ID etc. down by one).
// parseFile() builds a column map from the HEADER ROW (buildColMap) so any order
// works; this static map is only used when a header can't be matched.
//
// [0]  Event Date          [1]  Event Time          [2]  Pay Period
// [3]  Ordinary/Exp (A)    [4]  Signature (B)       [5]  Call Returns (C)
// [6]  eParcel Consign(D)  [7]  eParcel Addl (E)    [8]  Drop Rate Service
// [9]  Shuttle Rate        [10] DET Service          [11] Trans.Type
// [12] Reason              [13] Description          [14] Driver Name
// [15] AP Signoff ID       [16] Signoff Workcentre   [17] Status
// [18] Article Unknown (U)

const COL = {
  DATE:0, TIME:1, PAY_PERIOD:2,
  SVC_A:3, SVC_B:4, SVC_C:5, SVC_D:6, SVC_E:7,
  DROP_RATE:8, SHUTTLE:9, DET:10,
  TRANS_TYPE:11, REASON:12, DESCRIPTION:13,
  DRIVER:14, SIGNOFF:15, WORKCENTRE:16, STATUS:17,
  UNKNOWN:18,
};

// Match each logical column to its actual index by header text, so column order
// doesn't matter. First matching rule wins; unmatched keys fall back to COL above.
const HEADER_RULES = [
  ['DATE',        /^EVENT DATE/],
  ['TIME',        /^EVENT TIME/],
  ['PAY_PERIOD',  /^PAY PERIOD/],
  ['SVC_A',       /^ORDINARY/],
  ['SVC_B',       /^SIGNATURE/],
  ['SVC_C',       /^CALL FOR/],
  ['SVC_D',       /^EPARCEL CONSIGN/],
  ['SVC_E',       /^EPARCEL ADD/],
  ['DROP_RATE',   /^DROP RATE/],
  ['SHUTTLE',     /^SHUTTLE/],
  ['DET',         /^DET\b/],
  ['UNKNOWN',     /ARTICLE UNKNOWN|UNKNOWN \( ?U/],
  ['TRANS_TYPE',  /^TRANS/],
  ['REASON',      /^REASON/],
  ['DESCRIPTION', /^DESCRIPTION/],
  ['DRIVER',      /^DRIVER/],
  ['SIGNOFF',     /SIGN ?-? ?OFF ID/],   // "AP Signoff ID" (must contain ID; excludes "Signoff Workcentre")
  ['WORKCENTRE',  /WORKCENTRE/],
  ['STATUS',      /^STATUS/],
];

/** Build a {KEY: colIndex} map from a header row, falling back to static COL. */
function buildColMap(headerRow) {
  const norm = (h) => (h == null ? '' : h.toString().trim().toUpperCase().replace(/\s+/g, ' '));
  const map = {};
  (headerRow || []).forEach((h, i) => {
    const H = norm(h);
    if (!H) return;
    for (const [key, re] of HEADER_RULES) {
      if (map[key] === undefined && re.test(H)) { map[key] = i; break; }
    }
  });
  return { ...COL, ...map };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function parseDate(val) {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val === 'number') {
    // Excel serial date
    const d = XLSX.SSF.parse_date_code(val);
    return d ? new Date(d.y, d.m - 1, d.d) : null;
  }
  // String: try DD/MM/YYYY then ISO
  if (typeof val === 'string') {
    const slash = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slash) return new Date(+slash[3], +slash[2]-1, +slash[1]);
    const iso = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return new Date(+iso[1], +iso[2]-1, +iso[3]);
  }
  return null;
}

/** DD/MM/YYYY — Sheet1 date format (used for both Event Date and Helper Cell1) */
function toAU(d) {
  if (!d) return '';
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

/** DD/MM/YYYY — same format as existing Sheet1 rows (e.g. 13/05/2026) */
function toEventDate(d) {
  return toAU(d);
}

/** ISO YYYY-MM-DD for period comparisons */
function toISO(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

/**
 * Express detection from AP Signoff ID (index 15 in the raw XLSX).
 * Handles: "E EXPRESS", "E XPRESS", "E EXPRESS POST", "E EXPREES",
 *          "E POST", "X PRES" (torresj abbreviation), etc.
 */
function isExpress(signoffId) {
  if (!signoffId) return false;
  const s = signoffId.toString().trim().toUpperCase();
  if (/^E[\s_]/.test(s)) return true;        // E EXPRESS, E XPRESS, E POST …
  if (/\bEXP(RESS)?\b/.test(s)) return true; // EXP, EXPRESS, EXPREES (typo)
  if (/X[_\s]?PRESS/i.test(s)) return true;  // XPRESS, X_PRESS
  if (/^X\s+PRES/.test(s)) return true;      // X PRES (torresj abbreviation)
  return false;
}

/** On-road scan: AP Signoff ID starts with "O " (e.g. "O ROAD", "O NROAD") */
function isOnRoad(signoffId) {
  if (!signoffId) return false;
  const s = signoffId.toString().trim().toUpperCase();
  return /^O[\s_]/.test(s);
}

/** Parse event time into "h:MM:SS am/pm" — matching existing Sheet1 format (e.g. "7:11:53 am") */
function parseTime(val) {
  if (!val) return '';
  const fmt12 = (h, m, s) => {
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12  = h % 12 || 12;
    return `${h12}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')} ${ampm}`;
  };
  if (val instanceof Date) {
    return fmt12(val.getHours(), val.getMinutes(), val.getSeconds());
  }
  if (typeof val === 'number') {
    const totalSecs = Math.round(val * 86400);
    return fmt12(Math.floor(totalSecs / 3600) % 24, Math.floor((totalSecs % 3600) / 60), totalSecs % 60);
  }
  if (typeof val === 'string') {
    // "7:11:53 am" or "7:11:53" or "07:11:53"
    const m = val.match(/(\d{1,2}):(\d{2}):(\d{2})\s*(am|pm)?/i);
    if (m) {
      let h = parseInt(m[1]);
      const ap = (m[4] || '').toLowerCase();
      if (ap === 'pm' && h !== 12) h += 12;
      if (ap === 'am' && h === 12) h = 0;
      return fmt12(h, parseInt(m[2]), parseInt(m[3]));
    }
    // "7:11 am" — no seconds
    const m2 = val.match(/(\d{1,2}):(\d{2})\s*(am|pm)?/i);
    if (m2) {
      let h = parseInt(m2[1]);
      const ap = (m2[3] || '').toLowerCase();
      if (ap === 'pm' && h !== 12) h += 12;
      if (ap === 'am' && h === 12) h = 0;
      return fmt12(h, parseInt(m2[2]), 0);
    }
  }
  return '';
}

// What the parcel store is called on screen. Hollypaul's operators know it as Sheet1 (the
// Google Sheet it replaced); a second business never had that sheet.
const STORE = IS_TENANT ? 'the parcel records' : 'Sheet1';
// Both reads live in sheet1-utils-sb. Hollypaul reaches them through the appsscript proxy;
// a tenant build doesn't deploy the proxy, so it calls the function directly.
const SHEET1_READS = IS_TENANT ? '/.netlify/functions/sheet1-utils-sb' : '/.netlify/functions/appsscript';

// ── Main Component ────────────────────────────────────────────────────────────

export default function ImportParcelsPage() {
  const { sheetData, sheetLoading, activePeriodISO, setActivePeriodISO, personnel, addPendingOnboarding } = useApp();
  const navigate = useNavigate();
  const controls = sheetData?.controls;

  const fileRef        = useRef();
  const anchorISORef   = useRef('');   // most-recent period-end anchor from Controls tab

  // Keep anchorISORef current and auto-select the period containing today
  useEffect(() => {
    const raw = controls?.reportEndDate;
    if (!raw) return;
    const mm = {Jan:'01',Feb:'02',Mar:'03',Apr:'04',May:'05',Jun:'06',Jul:'07',Aug:'08',Sep:'09',Oct:'10',Nov:'11',Dec:'12'};
    const p = raw.split('-');
    if (p.length === 3) {
      const anchorISO = `${p[2]}-${mm[p[1]] || '00'}-${p[0].padStart(2,'0')}`;
      anchorISORef.current = anchorISO;
      // Default the period selector to whichever 28-day cycle contains today
      const todayISO = toISO(new Date());
      setActivePeriodISO(detectPeriodEnd(todayISO, anchorISO));
    }
  }, [controls?.reportEndDate, setActivePeriodISO]);

  const [step, setStep]         = useState(0);   // 0=upload  1=preview  2=done
  const [fileName, setFileName] = useState('');
  const [preview, setPreview]   = useState([]);  // aggregated rows ready to write
  const [stats, setStats]       = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Delta check vs rows already in Sheet1 (parcel_events) for this file's dates.
  // null = not run · {checking} = in flight · {error} = failed (falls back to the
  // coarse per-date guard) · {dupCount,newCount,...} = diff ready, dup rows flagged.
  const [dupInfo, setDupInfo]   = useState(null);
  const [writeAll, setWriteAll] = useState(false); // override: write every row even if already imported

  // ── Period comes from the shared context (same value as RCTI Build Period) ──
  const periodEndDate   = activePeriodISO ? new Date(activePeriodISO + 'T12:00:00') : null;
  const periodStartDate = periodEndDate
    ? (() => { const d = new Date(periodEndDate); d.setDate(d.getDate() - 27); return d; })()
    : null;
  const periodEndISO    = activePeriodISO || '';

  // ── Delta check: which of the parsed rows are ALREADY in Sheet1? ───────────
  // Handles the "corrected WAHR" case: AP re-issues a report where most rows were
  // already imported (e.g. a driver was missing from the first file because of a
  // scanner comms issue). Multiset diff — each existing DB row can absorb at most
  // one file row, so legitimately repeated signoffs still count correctly. Match
  // is strict (driver+date+time+signoff+total) with a loose fallback that ignores
  // the event time, in case AP re-stamps times when regenerating the report.
  const runDeltaCheck = useCallback(async (rows) => {
    setDupInfo({ checking: true });
    setWriteAll(false);
    try {
      const datesISO = [...new Set(rows.map(r => r.dateISO).filter(Boolean))];
      const res = await fetch(SHEET1_READS, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getSheet1DayRows', datesISO }),
      }).then(r => r.json());
      if (!res?.success) throw new Error(res?.error || 'Delta check failed');
      const existing = res.rows || [];

      const norm = (s) => (s == null ? '' : String(s)).trim().toUpperCase();
      const strictK = (drv, iso, time, sig, tot) => [norm(drv), iso, norm(time), norm(sig), tot | 0].join('|');
      const looseK  = (drv, iso, sig, tot)       => [norm(drv), iso, norm(sig), tot | 0].join('|');
      const bump = (m, k, d) => m.set(k, (m.get(k) || 0) + d);

      const strictCnt = new Map(), looseCnt = new Map();
      existing.forEach(e => {
        const iso = (e.event_date_iso || '').substring(0, 10);
        bump(strictCnt, strictK(e.driver_name, iso, e.event_time, e.ap_signoff_id, e.total), 1);
        bump(looseCnt,  looseK(e.driver_name, iso, e.ap_signoff_id, e.total), 1);
      });

      // Pass 1: strict match (consumes both counters). Pass 2: loose fallback.
      const dupIdx = new Set();
      rows.forEach((r, i) => {
        const sk = strictK(r.driverName, r.dateISO, r.eventTime, r.apSignOffId, r.total);
        if ((strictCnt.get(sk) || 0) > 0) {
          bump(strictCnt, sk, -1);
          bump(looseCnt, looseK(r.driverName, r.dateISO, r.apSignOffId, r.total), -1);
          dupIdx.add(i);
        }
      });
      rows.forEach((r, i) => {
        if (dupIdx.has(i)) return;
        const lk = looseK(r.driverName, r.dateISO, r.apSignOffId, r.total);
        if ((looseCnt.get(lk) || 0) > 0) { bump(looseCnt, lk, -1); dupIdx.add(i); }
      });

      const newByDriver = {};
      rows.forEach((r, i) => {
        if (dupIdx.has(i)) return;
        if (!newByDriver[r.driverName]) newByDriver[r.driverName] = { rows: 0, parcels: 0 };
        newByDriver[r.driverName].rows++;
        newByDriver[r.driverName].parcels += r.total;
      });

      setPreview(prev => prev.map((r, i) => (dupIdx.has(i) ? { ...r, duplicate: true } : r)));
      setDupInfo({
        dupCount: dupIdx.size,
        newCount: rows.length - dupIdx.size,
        existingCount: existing.length,
        // existing DB rows this file doesn't account for — a big number here means
        // the diff may be unreliable (wrong file, or AP changed the row contents)
        existingUnmatched: existing.length - dupIdx.size,
        newByDriver,
      });
    } catch (err) {
      setDupInfo({ error: err.message });
    }
  }, []);

  // ── Parse & aggregate incoming XLSX ────────────────────────────────────────
  const parseFile = useCallback((file) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb  = XLSX.read(e.target.result, { type: 'array', cellDates: true });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        // header:1 → raw arrays; skip row 0 (headers)
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
        // Map logical columns from the header row so any column order works.
        const C = buildColMap(raw[0] || []);
        const dataRows = raw.slice(1).filter(r => r && r.length > 0);

        // Individual rows — one per AP signoff record (no aggregation)
        const rows = [];
        let skipped = 0;

        dataRows.forEach(r => {
          const driver  = (r[C.DRIVER] || '').toString().trim().toUpperCase();
          const dateRaw = r[C.DATE];
          const d       = parseDate(dateRaw);
          const svcA     = Number(r[C.SVC_A])   || 0;  // Ordinary/Express (A)
          const svcB     = Number(r[C.SVC_B])   || 0;  // Signature Items  (B)
          const svcC     = Number(r[C.SVC_C])   || 0;  // Call Returns     (C)
          const svcD     = Number(r[C.SVC_D])   || 0;  // eParcel Consign  (D)
          const svcE     = Number(r[C.SVC_E])   || 0;  // eParcel Addl     (E)
          const dropRate = Number(r[C.DROP_RATE]) || 0; // Drop Rate
          const shuttle  = Number(r[C.SHUTTLE]) || 0;  // Shuttle Rate
          const det      = Number(r[C.DET])     || 0;  // DET Service
          const unknown  = Number(r[C.UNKNOWN]) || 0;  // Article Unknown (U)
          // Total matches the formula confirmed correct by Python on the real file
          const total = svcA + svcB + svcC + svcD + svcE + unknown;

          if (!driver || !d || total <= 0) { skipped++; return; }

          const signoffId = (r[C.SIGNOFF] || '').toString().trim(); // "E EXPRESS", "V ASTORGA", "O NROAD" …
          const type      = isExpress(signoffId) ? 'Exp' : 'Parcel';
          const dateISO   = toISO(d);

          // Auto-detect Hollypaul pay period from scan date. detectPeriodEnd only
          // needs ANY Saturday on the 28-day grid for alignment, so fall back to the
          // static anchor when the Controls anchor hasn't loaded yet — never stamp the
          // raw scan date as the pay period (that mis-files the day under its own date
          // instead of the period end, and the day then vanishes from the EP tax
          // invoice / any pay_period-keyed reader).
          const anchor = anchorISORef.current || PERIOD_ANCHOR_STATIC;
          const hollyPayPeriod = detectPeriodEnd(dateISO, anchor);

          rows.push({
            driverName:  driver,
            helperCell1: toAU(d),          // computed DD/MM/YYYY (not in XLSX)
            eventDate:   toEventDate(d),   // DD/MM/YYYY — Sheet1 col A
            eventTime:   parseTime(r[C.TIME]),
            payPeriod:   hollyPayPeriod,   // Hollypaul 28-day period end (auto-detected)
            parcelType:  type,
            apSignOffId: signoffId,
            transType:   (r[C.TRANS_TYPE]  || '').toString().trim(),
            reason:      (r[C.REASON]      || '').toString().trim(),
            description: (r[C.DESCRIPTION] || '').toString().trim(),
            workcentre:  (r[C.WORKCENTRE]  || '').toString().trim(),
            status:      (r[C.STATUS]      || '').toString().trim(),
            svcA, svcB, svcC, svcD, svcE, dropRate, shuttle, det, unknown,
            total,
            dateISO,
          });
        });

        // Sort: driver → date → time
        rows.sort((a, b) => {
          if (a.driverName < b.driverName) return -1;
          if (a.driverName > b.driverName) return  1;
          const dc = a.dateISO.localeCompare(b.dateISO);
          if (dc !== 0) return dc;
          return a.eventTime.localeCompare(b.eventTime);
        });

        // Summary stats
        const drivers      = [...new Set(rows.map(r => r.driverName))];
        const dates        = [...new Set(rows.map(r => r.dateISO))].sort();
        const totalParcels = rows.reduce((s, r) => s + r.total, 0);
        const expTotal     = rows.filter(r => r.parcelType === 'Exp').reduce((s, r) => s + r.total, 0);
        const expRows      = rows.filter(r => r.parcelType === 'Exp').length;
        const onRoadTotal  = rows.filter(r => isOnRoad(r.apSignOffId)).reduce((s, r) => s + r.total, 0);

        // Auto-set the Build Period selector to match the detected pay period
        // (uses the first row's period — all rows from the same daily export share it)
        if (rows.length > 0 && anchorISORef.current) {
          setActivePeriodISO(rows[0].payPeriod);
        }

        setPreview(rows);
        setStats({ drivers, dates, totalParcels, expRows, expTotal, onRoadTotal, skipped });
        setStep(1);
        runDeltaCheck(rows);
      } catch (err) {
        alert('Could not parse the file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  }, [runDeltaCheck]);

  const onFileChange = (e) => { if (e.target.files[0]) parseFile(e.target.files[0]); };
  const onDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  }, [parseFile]);

  // Rows the write button will actually send: when the delta check found rows
  // already in Sheet1 (and the operator hasn't overridden), only the new ones.
  const deltaActive  = !writeAll && (dupInfo?.dupCount || 0) > 0;
  const rowsToWrite  = deltaActive ? preview.filter(r => !r.duplicate) : preview;

  // ── Write to Sheet1 ─────────────────────────────────────────────────────────
  const doImport = async () => {
    if (rowsToWrite.length === 0) return;
    setImporting(true);
    setImportResult(null);
    try {
      // Coarse duplicate-upload guard: warn if Sheet1 already holds rows for any
      // of this file's dates (both BOUVENGJ and BOUVENGP2 upload — a second pass
      // would double every count). Skipped when the row-level delta check already
      // filtered the duplicates out — that's the whole point of a delta import.
      if (!deltaActive) {
        try {
          const dates = [...new Set(rowsToWrite.map(r => r.eventDate).filter(Boolean))];
          const chk = await fetch(SHEET1_READS, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'checkSheet1Dates', dates }),
          }).then(r => r.json());
          const existing = Object.entries(chk?.counts || {}).filter(([, v]) => v.rows > 0);
          if (existing.length > 0) {
            const lines = existing.map(([d, v]) => `  • ${d}: ${v.rows} rows / ${v.parcels.toLocaleString()} parcels already in ${STORE}`).join('\n');
            const ok = window.confirm(
              `⚠ Possible duplicate upload!\n\n${IS_TENANT ? 'The parcel records ALREADY have' : 'Sheet1 ALREADY has'} data for date(s) in this file:\n${lines}\n\n` +
              `Someone may have uploaded this file already (more than one person uploads).\n\n` +
              `Press OK to write anyway (counts will be doubled), or Cancel to stop.`
            );
            if (!ok) { setImporting(false); return; }
          }
        } catch { /* guard is best-effort — never block the import on a check failure */ }
      }

      const res = await fetch('/.netlify/functions/write-sheet1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: rowsToWrite }),
      });
      const json = await res.json();
      setImportResult(json);
      if (json.success) {
        // Queue any unrecognised drivers as Dashboard onboarding alerts
        if (unknownDrivers.length > 0) addPendingOnboarding(unknownDrivers);
        setStep(2);
      }
    } catch (err) {
      setImportResult({ success: false, error: err.message });
    } finally {
      setImporting(false);
    }
  };

  const resetAll = () => {
    setStep(0); setFileName(''); setPreview([]);
    setStats(null); setImportResult(null);
    setDupInfo(null); setWriteAll(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  // ── Live period validation — recomputes whenever the Build Period changes ────
  const isOutsidePeriod = (dateISO) => {
    if (!periodStartDate || !periodEndDate) return false;
    const ts = new Date(dateISO + 'T12:00:00').getTime();
    return ts < periodStartDate.getTime() || ts > periodEndDate.getTime();
  };
  const liveOutsideCount = preview.filter(r => isOutsidePeriod(r.dateISO)).length;

  // Cross-reference driver logins against onboarded personnel
  const knownLogins = new Set((personnel || []).map(p => (p.loginName || '').toUpperCase()));
  const unknownDrivers = [...new Set(preview.map(r => r.driverName))]
    .filter(d => d && !knownLogins.has(d))
    .sort();

  // ── Styles ───────────────────────────────────────────────────────────────────
  const card = {
    background: 'var(--hp-surface)',
    border: '1px solid var(--hp-border)',
    borderRadius: 'var(--radius)',
    padding: 24,
  };
  const btnPrimary = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '10px 20px', background: 'var(--hp-accent)', color: '#fff',
    border: 'none', borderRadius: 'var(--radius)',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  };
  const btnSecondary = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '10px 16px', background: 'var(--hp-surface)', color: 'var(--hp-dim)',
    border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)',
    fontSize: 13, fontWeight: 500, cursor: 'pointer',
  };
  const pill = (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 999,
    fontSize: 11, fontWeight: 600,
    background: `${color}22`, color: color,
  });
  const statBox = {
    background: 'var(--hp-bg)', border: '1px solid var(--hp-border)',
    borderRadius: 'var(--radius)', padding: '12px 16px', textAlign: 'center',
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 32, maxWidth: 960, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Import Parcel Volume</h1>
        <p style={{ color: 'var(--hp-dim)', fontSize: 13, margin: '6px 0 0' }}>
          Upload the Australia Post XLSX report — the Engine reads every column,
          calculates daily totals per driver, and writes directly to {STORE}.
        </p>
      </div>

      {/* ── Step 0: Upload ──────────────────────────────────────────────────── */}
      {step === 0 && (
        <div style={card}>
          {/* Build Period selector — same options as RCTI page, shared via context */}
          {(() => {
            const sheetEndISO = sheetData?.controls?.reportEndDate
              ? (() => {
                  const raw = sheetData.controls.reportEndDate;
                  const mm = {Jan:'01',Feb:'02',Mar:'03',Apr:'04',May:'05',Jun:'06',Jul:'07',Aug:'08',Sep:'09',Oct:'10',Nov:'11',Dec:'12'};
                  const p = raw.split('-');
                  return p.length === 3 ? `${p[2]}-${mm[p[1]]||'00'}-${p[0].padStart(2,'0')}` : '';
                })()
              : '';

            // Same period list as RCTIPage: 1 ahead + current + 12 back
            // Local date formatting avoids UTC timezone shift (which was pushing dates to Fridays)
            const toLocalISO = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            const snapToSaturday = d => { const s = new Date(d); const day = s.getDay(); if (day !== 6) s.setDate(s.getDate() - ((day + 1) % 7)); return s; };
            const periods = [];
            if (sheetEndISO) {
              const anchor = snapToSaturday(new Date(sheetEndISO + 'T00:00:00'));
              for (let offset = -1; offset <= 12; offset++) {
                const end = new Date(anchor);
                end.setDate(anchor.getDate() - offset * 28);
                periods.push(toLocalISO(end));
              }
            }

            return (
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'rgba(99,102,241,0.06)', border:'1px solid rgba(99,102,241,0.2)', borderRadius:'var(--radius)', marginBottom:20, flexWrap:'wrap' }}>
                <span style={{ fontSize:11, fontWeight:600, color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>Build Period</span>

                {sheetLoading
                  ? <span style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--hp-dim)' }}><Loader size={13} style={{ animation:'spin 1s linear infinite' }} /> Loading…</span>
                  : periods.length > 0
                    ? <select
                        value={activePeriodISO || sheetEndISO}
                        onChange={e => setActivePeriodISO(e.target.value)}
                        style={{ padding:'6px 10px', background:'var(--hp-bg)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', color:'var(--hp-text)', fontSize:13, fontFamily:'var(--font-body)', flex:1, maxWidth:360 }}
                      >
                        {periods.map((iso, i) => (
                          <option key={iso} value={iso}>
                            {isoToPeriodLabel(iso)}
                            {i === 0 ? ' — next period' : i === 1 ? (IS_TENANT ? ' — current' : ' — current (from Sheet)') : ''}
                          </option>
                        ))}
                      </select>
                    : <span style={{ fontSize:13, color:'#d97706', display:'flex', alignItems:'center', gap:6 }}><AlertCircle size={13} /> Sheet not loaded — refresh the page</span>
                }

                {activePeriodISO && (
                  <span style={{ fontSize:11, color:'var(--hp-dim)', marginLeft:'auto', whiteSpace:'nowrap' }}>AP column C ignored</span>
                )}
              </div>
            );
          })()}

          <div
            onDrop={onDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => fileRef.current?.click()}
            style={{
              border: '2px dashed var(--hp-border)', borderRadius: 'var(--radius)',
              padding: '56px 32px', textAlign: 'center', cursor: 'pointer',
              transition: 'border-color 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--hp-accent)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--hp-border)'}
          >
            <FileSpreadsheet size={44} style={{ color: 'var(--hp-accent)', marginBottom: 14 }} />
            <p style={{ fontWeight: 600, margin: '0 0 6px', fontSize: 15 }}>
              Drop your AP XLSX report here
            </p>
            <p style={{ color: 'var(--hp-dim)', fontSize: 13, margin: 0 }}>
              or click to browse &nbsp;·&nbsp; <span style={{ opacity: 0.6 }}>.xlsx · .xls · .csv</span>
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={onFileChange}
          />
        </div>
      )}

      {/* ── Step 1: Preview ─────────────────────────────────────────────────── */}
      {step === 1 && stats && (
        <div>
          {/* Action bar — always visible at top */}
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, padding:'16px 20px', background:'#4f46e5', borderRadius:'var(--radius)' }}>
            <button
              onClick={doImport}
              disabled={importing || dupInfo?.checking || rowsToWrite.length === 0}
              style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'12px 28px', background:'#fff', color:'#4f46e5', border:'none', borderRadius:'var(--radius)', fontSize:14, fontWeight:700, cursor:'pointer', opacity: (dupInfo?.checking || rowsToWrite.length === 0) ? 0.6 : 1 }}
            >
              {importing
                ? <><Loader size={14} style={{ animation:'spin 1s linear infinite' }} /> Writing to {STORE}…</>
                : dupInfo?.checking
                  ? <><Loader size={14} style={{ animation:'spin 1s linear infinite' }} /> Checking {STORE}…</>
                  : deltaActive
                    ? <><Upload size={14} /> Write {rowsToWrite.length} NEW row{rowsToWrite.length !== 1 ? 's' : ''} to {STORE}</>
                    : <><Upload size={14} /> Write {rowsToWrite.length} rows to {STORE}</>}
            </button>
            <span style={{ color:'rgba(255,255,255,0.8)', fontSize:13 }}>
              {stats.totalParcels.toLocaleString()} parcels · {preview.length} rows · {fileName}
            </span>
            <button style={{ marginLeft:'auto', display:'inline-flex', alignItems:'center', gap:6, padding:'8px 14px', background:'rgba(255,255,255,0.15)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', borderRadius:'var(--radius)', fontSize:12, cursor:'pointer' }} onClick={resetAll}>
              <ChevronLeft size={13} /> Different file
            </button>
          </div>

          {/* Stat bar */}
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Drivers',        value: stats.drivers.length,                   color: null },
              { label: 'Days',           value: stats.dates.length,                     color: null },
              { label: 'Total Parcels',  value: stats.totalParcels.toLocaleString(),    color: null },
              { label: 'Express Post',   value: (stats.expTotal || 0).toLocaleString(), color: '#d97706' },
              { label: 'On Road Scans',  value: (stats.onRoadTotal || 0).toLocaleString(), color: '#6366f1' },
              { label: IS_TENANT ? 'Rows' : 'Sheet1 Rows',    value: preview.length,                         color: null },
            ].map(({ label, value, color }) => (
              <div key={label} style={statBox}>
                <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--hp-accent)' }}>{value}</div>
                <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Delta check vs rows already imported */}
          {dupInfo && !dupInfo.checking && !dupInfo.error && dupInfo.dupCount > 0 && (
            <div style={{ padding:'12px 16px', background:'rgba(34,197,94,0.07)', border:'1px solid rgba(34,197,94,0.35)', borderRadius:'var(--radius)', marginBottom:12, fontSize:13 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, color:'#16a34a', fontWeight:600 }}>
                <CheckCircle2 size={15} style={{ flexShrink:0 }} />
                <span>
                  {dupInfo.dupCount.toLocaleString()} of {preview.length.toLocaleString()} rows are already in {STORE}
                  {dupInfo.newCount > 0
                    ? <> — only the <strong>{dupInfo.newCount} new row{dupInfo.newCount !== 1 ? 's' : ''}</strong> will be written.</>
                    : <> — there is nothing new to import.</>}
                </span>
              </div>
              {dupInfo.newCount > 0 && (
                <div style={{ marginTop:6, marginLeft:23, color:'var(--hp-text)' }}>
                  New: {Object.entries(dupInfo.newByDriver).map(([d, v]) =>
                    `${d} (${v.rows} row${v.rows !== 1 ? 's' : ''} · ${v.parcels.toLocaleString()} parcels)`).join(', ')}
                </div>
              )}
              {dupInfo.existingUnmatched > 0 && (
                <div style={{ marginTop:6, marginLeft:23, color:'#d97706' }}>
                  ⚠ {dupInfo.existingUnmatched} row{dupInfo.existingUnmatched !== 1 ? 's' : ''} already in {STORE} for
                  {' '}{dupInfo.existingUnmatched !== 1 ? 'these dates don’t' : 'these dates doesn’t'} match anything in this file
                  — check it&apos;s the right file before writing.
                </div>
              )}
              <label style={{ display:'flex', alignItems:'center', gap:6, marginTop:8, marginLeft:23, color:'var(--hp-dim)', fontSize:12, cursor:'pointer', userSelect:'none' }}>
                <input type="checkbox" checked={writeAll} onChange={e => setWriteAll(e.target.checked)} />
                Write ALL {preview.length} rows anyway (will double the {dupInfo.dupCount} already-imported row{dupInfo.dupCount !== 1 ? 's' : ''})
              </label>
            </div>
          )}
          {dupInfo?.error && (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'var(--radius)', marginBottom:12, fontSize:13, color:'#d97706' }}>
              <AlertCircle size={15} />
              <span>Couldn&apos;t check {STORE} for already-imported rows ({dupInfo.error}) — the per-date duplicate warning will still run before writing.</span>
            </div>
          )}

          {/* Warnings */}
          {liveOutsideCount > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'var(--radius)', marginBottom:12, fontSize:13, color:'#d97706' }}>
              <AlertCircle size={15} />
              <span>
                <strong>{liveOutsideCount}</strong> row{liveOutsideCount !== 1 ? 's' : ''} have dates outside the
                selected period ({isoToPeriodLabel(activePeriodISO)}). They will still be written — the Engine filters them during RCTI calculation.
              </span>
            </div>
          )}
          {stats.expRows > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'rgba(217,119,6,0.06)', border:'1px solid rgba(217,119,6,0.25)', borderRadius:'var(--radius)', marginBottom:12, fontSize:13, color:'#d97706' }}>
              <Info size={15} />
              <span>
                <strong>{stats.expRows}</strong> Express Post row{stats.expRows !== 1 ? 's' : ''} detected and <strong>included</strong> in this import.
                Written to {STORE} with Parcel type = <strong>Exp</strong> alongside the regular Parcel rows.
              </span>
            </div>
          )}

          {unknownDrivers.length > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 14px', background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'var(--radius)', marginBottom:12, fontSize:12, color:'#ef4444' }}>
              <AlertCircle size={13} style={{ flexShrink:0 }} />
              <span>
                <strong>{unknownDrivers.length} driver{unknownDrivers.length !== 1 ? 's' : ''} not yet onboarded</strong>
                {' '}({unknownDrivers.join(', ')}) — rows highlighted below.
                {IS_TENANT ? 'Add them to the driver list so their names show.' : 'A reminder will appear on the Dashboard after writing.'}
              </span>
            </div>
          )}

          {/* Preview table */}
          <div style={{ ...card, overflowX: 'auto', marginBottom: 16 }}>
            <p style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 600, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Preview — {preview.length} rows to write &nbsp;·&nbsp; file: {fileName}
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {['Driver Name', 'Event Date', 'Time', 'AP Sign Off ID', 'Type', 'Total', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--hp-border)', color: 'var(--hp-dim)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => {
                  const isUnknown = !knownLogins.has(r.driverName);
                  const isDup     = deltaActive && r.duplicate;
                  return (
                  <tr key={i} style={{ opacity: isDup ? 0.45 : 1, background: isUnknown ? 'rgba(239,68,68,0.06)' : isOutsidePeriod(r.dateISO) ? 'rgba(245,158,11,0.04)' : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--hp-border)', fontWeight: 600 }}>{r.driverName}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--hp-border)' }}>{r.eventDate}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--hp-border)', color: 'var(--hp-dim)' }}>{r.eventTime}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--hp-border)', fontFamily: 'monospace', fontSize: 11 }}>
                      {r.apSignOffId
                        ? <span style={{ color: isOnRoad(r.apSignOffId) ? '#6366f1' : isExpress(r.apSignOffId) ? '#d97706' : 'var(--hp-text)' }}>{r.apSignOffId}</span>
                        : <span style={{ color: 'var(--hp-dim)' }}>—</span>}
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--hp-border)' }}>
                      <span style={pill(r.parcelType === 'Exp' ? '#d97706' : '#22c55e')}>{r.parcelType}</span>
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--hp-border)', fontWeight: 700, textAlign: 'right' }}>{r.total}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid var(--hp-border)', whiteSpace: 'nowrap' }}>
                      {isDup && <span style={pill('#94a3b8')}>already in {STORE}</span>}
                      {!isDup && deltaActive && <span style={pill('#22c55e')}>new</span>}
                      {isUnknown && <span style={{ ...pill('#ef4444'), marginLeft: 4 }}>not onboarded</span>}
                      {!isUnknown && isOutsidePeriod(r.dateISO) && <span style={{ ...pill('#d97706'), marginLeft: 4 }}>outside period</span>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {importResult && !importResult.success && (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'var(--radius)', marginBottom:12, fontSize:13, color:'#ef4444' }}>
              <AlertCircle size={15} /> {importResult.error}
              {importResult.errors && <ul style={{ margin:'4px 0 0 16px', padding:0 }}>{importResult.errors.map((e,i) => <li key={i}>{e}</li>)}</ul>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button style={btnSecondary} onClick={resetAll}><ChevronLeft size={14} /> Upload different file</button>
          </div>
        </div>
      )}

      {/* ── Step 2: Done ────────────────────────────────────────────────────── */}
      {step === 2 && importResult && (
        <div style={{ ...card, textAlign: 'center', padding: '48px 32px' }}>
          <CheckCircle2 size={52} style={{ color: '#22c55e', marginBottom: 16 }} />
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>
            {importResult.written} rows written to {STORE}
          </h2>
          <p style={{ color: 'var(--hp-dim)', fontSize: 14, margin: '0 0 32px' }}>
            {IS_TENANT
              ? 'Parcel Audit and the Daily Snapshot show the new rows straight away.'
              : <>The Engine will pick up the new data on the next sheet refresh.
            Formula helper cells (Helper Cell1 &amp; Parcel type) have been auto-populated.</>}
          </p>
          <button
            onClick={resetAll}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '14px 32px',
              background: '#4f46e5', color: '#fff',
              border: 'none', borderRadius: 'var(--radius)',
              fontSize: 15, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(79,70,229,0.35)',
            }}
          >
            <Upload size={16} /> Import Another File
          </button>
        </div>
      )}

      <style>{`@keyframes spin { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }`}</style>
    </div>
  );
}
